// Activos fijos — DATOS REALES por cooperativa: registro de activos (con su comprobante) y
// depreciación mensual generada por el motor contable. Antes usaba datos de ejemplo.

import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { Add as AddIcon, Inventory as InventoryIcon } from '@mui/icons-material';
import EncabezadoOperativo from '../components/EncabezadoOperativo';
import FormularioDialog from '../components/FormularioDialog';
import TablaDatos from '../components/TablaDatos';
import useCargar from '../hooks/useCargar';
import useCooperativaOperativa from '../hooks/useCooperativaOperativa';
import { activosApi, tercerosApi } from '../services/coopnetApi';
import { fecha, fmt, hoy, moneda } from '../utils/formato';

const ActivosFijos = () => {
  const op = useCooperativaOperativa();
  const id = op.cooperativaId;
  const [dlg, setDlg] = useState(null);
  const [ok, setOk] = useState('');

  const { datos, cargando, error, recargar } = useCargar(async () => {
    if (!id) return null;
    const [activos, proveedores] = await Promise.all([activosApi.listar(id), tercerosApi.listar(id, 'Proveedor')]);
    return { activos, proveedores };
  }, [id]);

  const activos = datos?.activos || [];
  const proveedores = useMemo(() => datos?.proveedores || [], [datos]);
  const nombreProv = useMemo(() => new Map(proveedores.map((p) => [p.id, p.nombre])), [proveedores]);
  const total = activos.reduce((t, a) => t + (a.valor || 0), 0);

  return (
    <EncabezadoOperativo
      titulo="Activos fijos"
      subtitulo="Equipos y bienes de la cooperativa"
      operativa={op}
      acciones={
        <>
          <Button variant="outlined" onClick={() => setDlg('depreciacion')} disabled={activos.length === 0}>Generar depreciación mensual</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDlg('nuevo')}>Registrar activo</Button>
        </>
      }
    >
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {cargando && !datos ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 2, mb: 3 }}>
            {[['Activos registrados', fmt(activos.length)], ['Valor de compra', moneda(total)]].map(([t, v]) => (
              <Card key={t}><CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#E651001A', color: '#E65100', display: 'flex' }}><InventoryIcon /></Box>
                <Box><Typography variant="h5" fontWeight="bold" sx={{ color: '#E65100' }}>{v}</Typography><Typography variant="body2">{t}</Typography></Box>
              </CardContent></Card>
            ))}
          </Box>
          <TablaDatos
            filas={activos}
            vacio="Aún no hay activos fijos registrados."
            columnas={[
              { titulo: 'Activo', render: (a) => a.nombre },
              { titulo: 'Proveedor', render: (a) => nombreProv.get(a.terceroId) || '—' },
              { titulo: 'Compra', render: (a) => fecha(a.fechaCompra) },
              { titulo: 'Valor', align: 'right', render: (a) => moneda(a.valor) },
              { titulo: 'Vida útil', render: (a) => `${a.vidaUtilAnios} años` },
            ]}
          />
        </>
      )}

      <FormularioDialog
        abierto={dlg === 'nuevo'}
        titulo="Registrar activo fijo"
        descripcion={proveedores.length === 0 ? 'Primero registra el proveedor en «Compras y gastos».' : undefined}
        campos={[
          { campo: 'nombre', label: 'Nombre del activo', tipo: 'texto', requerido: true },
          { campo: 'terceroId', label: 'Proveedor', tipo: 'select', requerido: true, opciones: proveedores.map((p) => ({ valor: p.id, etiqueta: p.nombre })) },
          { campo: 'valor', label: 'Valor de compra', tipo: 'numero', requerido: true, positivo: true },
          { campo: 'vidaUtilAnios', label: 'Vida útil (años)', tipo: 'numero', requerido: true, positivo: true },
          { campo: 'fecha', label: 'Fecha de compra', tipo: 'fecha', requerido: true },
        ]}
        inicial={{ fecha: hoy(), vidaUtilAnios: 5 }}
        textoBoton="Registrar"
        onGuardar={async (v) => { await activosApi.registrar(id, { nombre: v.nombre, valor: v.valor, vidaUtilAnios: v.vidaUtilAnios, terceroId: v.terceroId, fecha: v.fecha }); setOk('Activo registrado.'); recargar(); }}
        onClose={() => setDlg(null)}
      />
      <FormularioDialog
        abierto={dlg === 'depreciacion'}
        titulo="Depreciación mensual"
        descripcion="Genera el comprobante de depreciación del mes de esa fecha para todos los activos."
        campos={[{ campo: 'fecha', label: 'Fecha', tipo: 'fecha', requerido: true }]}
        inicial={{ fecha: hoy() }}
        textoBoton="Generar"
        onGuardar={async (v) => { await activosApi.depreciacionMensual(id, { fecha: v.fecha }); setOk('Depreciación mensual generada.'); }}
        onClose={() => setDlg(null)}
      />
    </EncabezadoOperativo>
  );
};

export default ActivosFijos;
